// @ts-nocheck
import * as Sentry from '@sentry/react';
import jwt_decode from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { Request } from '@/components/authored-page-routes/authored-page-routes.interface';
import {
  banners,
  COOKIE_DELTA,
  cookies,
  DATASET_ATTRIBUTES_VALUES,
  environment,
  LOCALE,
  LoggedInStatus,
  TOKEN_REFRESH,
} from '@/data/constants';
import { pcidAuth } from 'Utils/sso/auth';
import { isLocalhost, isPharmaprixBanner, isProductionDomain } from 'Utils/utils';
import { ROUTES } from '@/utils';
import UniversalCookies, { CookieSetOptions } from 'universal-cookie';

const cookieStore = new UniversalCookies();

/**
 * @param req - if a request object is provided, assume SSR and use request headers to grab hostname, otherwise use
 * window
 */
export const getSiteId = (req?: Request): string => (isPharmaprixBanner(req) ? banners.PHARMAPRIX : banners.SHOPPERS);

/**
 * The customer cart guid as a LOGGEDIN user. If the guid does not exist yet, fall back to the guest cart guid
 */
export const getCustomerExistingCartGuid = (key = 'customer-cart'): string => {
  const siteId = getSiteId();
  const guid = cookieStore.get(`${siteId}-${key}`);
  // modifiedDate lets us know the last time an item was added to the bag as a LOGGEDIN user.
  const modifiedDate = cookieStore.get(`${siteId}-${key}-modified-date`);
  const cookieExpired = checkIfCustomerCookieExpired(modifiedDate);
  if (cookieExpired) return '';

  // Fall back to guest cart guid if guid does not exist
  return guid ? guid : getExistingCartGuid();
};

/**
 * Function to check if the cookie is expired where the modified date is not updated for more than 14 days.
 */
export const checkIfCustomerCookieExpired = (modifiedDate: string | undefined): boolean => {
  return (
    getLoggedInStatus() === LoggedInStatus.CURRENT &&
    new Date().getTime() > Number(modifiedDate) + Number(COOKIE_DELTA.TWO_DAYS)
  );
};

export const getIsModernizedBag = (): boolean => {
  if (typeof window !== 'undefined') {
    //@ts-ignore
    return String(window?.modernizeBag).toLowerCase() === 'true';
  }
  return false;
};

// The cart guid as an ANNONYMOUS user.
export const getExistingCartGuid = (): string => {
  const siteId = getSiteId();
  const heliosGuid = cookieStore.get(`${siteId}-cart-helios`);
  // modifiedDate lets us know the last time an item was added to the bag as an ANNOYMOUS user.
  const modifiedDate = cookieStore.get(`${siteId}-cart-helios-modified-date`);
  const cookieExpired = checkIfCookieExpired(modifiedDate);
  if (cookieExpired) return '';

  return heliosGuid || '';
};

/**
 * Function to check if the cookie is expired where the modified date is not updated for more than 14 days.
 */
export const checkIfCookieExpired = (modifiedDate: string | undefined): boolean => {
  return (
    getLoggedInStatus() === LoggedInStatus.ANONYMOUS &&
    //Commented out for BFCM
    //TODO: Post BFCM reset anonymous user cart cookie expiry back to 14 days, temp change to 2 days for BFCMM
    new Date().getTime() > Number(modifiedDate) + Number(COOKIE_DELTA.TWO_DAYS)
  );
};

/** @deprecated this token was for Hybris, which we are no longer using */
export const getAccessToken = async (): string => {
  const accessToken = cookieStore.get(cookies.ACCESS_TOKEN);
  return accessToken || '';
};

/**
 * @param req - if a request object is provided, assume SSR and use request cookies to grab token, otherwise try
 * document cookies
 */
export const getCiamAccessToken = (req?: Request): string | undefined => {
  const { UAT, PRODUCTION } = environment;

  const isUAT = req
    ? req.headers?.host.includes(UAT)
    : typeof window !== 'undefined' && window?.location?.hostname.includes(UAT);
  if (process.env.NODE_ENV === PRODUCTION && !isUAT) {
    const globalToken = req?.cookies?.ciamAccessTokenGlobal || cookieStore.get(cookies.CIAM_ACCESS_TOKEN_GLOBAL);
    if (globalToken) {
      return globalToken;
    }
  }

  return req?.cookies?.ciamAccessToken || cookieStore.get(cookies.CIAM_ACCESS_TOKEN);
};

/**
 * Returns authorization header with Bearer token if user has valid access token.
 * @deprecated This function has dual responsibility (checking auth state + returning header value).
 * Use `isUserAuthenticated()` to check if user is authenticated (based on refresh token),
 * or use `getAuthorizationHeader()` to get the authorization header value (based on access token).
 * @param req - if a request object is provided, assume SSR and use request cookies to grab token, otherwise try
 * document cookies
 * @returns contrary to its name, this function returns either an empty string or a string in the form `"Bearer ${TOKEN}"`
 */
export const isLoggedIn = (req?: Request): `Bearer ${string}` | '' => {
  //get the token
  const ciamAccessToken = getCiamAccessToken(req);

  //check if the token is available
  if (ciamAccessToken) {
    //check if token is not expired
    return `Bearer ${ciamAccessToken}`;
  }
  return '';
};

export const getLastViewedPage = () => {
  return window.localStorage.getItem(cookies.SSO_LAST_VISITED_PAGE) ?? '/';
};

export const setOneCheckoutCookies = (cartId: string, authToken: string) => {
  const defaultLocale = isPharmaprixBanner() ? LOCALE.FR : LOCALE.EN;
  const lang = cookieStore.get(cookies.USER_PREFERRED_LOCALE) || defaultLocale;
  const lob = isPharmaprixBanner() ? 'SDMPH' : 'SDM';

  // Create base cookie config
  const oneCheckoutCookieConfig: CookieSetOptions = {
    path: '/',
    secure: true,
    // for local development, we don't want to set the domain since the cookies are not shared with the domain
    ...(!isLocalhost() && { domain: isPharmaprixBanner() ? '.pharmaprix.ca' : '.shoppersdrugmart.ca' }),
  };

  if (authToken) {
    cookieStore.set(cookies.ONE_CHECKOUT_AUTH_TOKEN, authToken, oneCheckoutCookieConfig);
  }
  cookieStore.set(cookies.ONE_CHECKOUT_LOB, lob, oneCheckoutCookieConfig);
  cookieStore.set(cookies.ONE_CHECKOUT_CART_ID, cartId, oneCheckoutCookieConfig);
  cookieStore.set(cookies.ONE_CHECKOUT_LOCALE, lang, oneCheckoutCookieConfig);
};

export const saveTokens = (tokens, isOneCheckout?: boolean) => {
  if (tokens && tokens.refresh_token && tokens.access_token) {
    const { refresh_token, access_token } = tokens;
    const { exp, jti, pcid } = jwt_decode(access_token);
    Sentry.setUser({ id: pcid });

    // Add JWT metadata as context
    Sentry.setContext('saving auth_token', {
      tokenId: jti,
      exp: new Date(exp * 1000).toISOString(), // human-readable
    });
    /*
      Adding both cookies just in case.
     */
    if (process.env.NEXT_PUBLIC_APP_ENV === 'prod') {
      let expireDate = new Date();
      expireDate.setMonth(expireDate.getMonth() + 6);
      const accessTokenExpireDate = new Date(exp * 1000);
      cookieStore.set(cookies.CIAM_ACCESS_TOKEN, access_token, {
        expires: accessTokenExpireDate,
        path: '/',
      });
      cookieStore.set(cookies.SSO_REFRESH_TOKEN, refresh_token, {
        expires: expireDate,
        path: '/',
      });

      //save the id_token
      if (tokens.id_token) {
        const { id_token } = tokens;
        window.localStorage.setItem(cookies.SSO_ID_TOKEN, id_token);
      }
    } else {
      let expireDate = new Date();
      expireDate.setMonth(expireDate.getMonth() + 6);
      const accessTokenExpireDate = new Date(exp * 1000);
      cookieStore.set(cookies.CIAM_ACCESS_TOKEN, access_token, { expires: accessTokenExpireDate, path: '/' });
      cookieStore.set(cookies.SSO_REFRESH_TOKEN, refresh_token, { expires: expireDate, path: '/' });
      if (tokens.id_token) {
        const { id_token } = tokens;
        window.localStorage.setItem(cookies.SSO_ID_TOKEN, id_token);
      }
    }

    if (isOneCheckout) {
      const existingCartGuid = isUserAuthenticated() ? getCustomerExistingCartGuid() : getExistingCartGuid();
      setOneCheckoutCookies(existingCartGuid, tokens.access_token);
    }
  }
};

export const getTokens = () => {
  return {
    access_token: getCiamAccessToken(),
    refresh_token: cookieStore.get(cookies.SSO_REFRESH_TOKEN),
    id_token: window.localStorage.getItem(cookies.SSO_ID_TOKEN),
  };
};

export const getRefreshToken = () => {
  return cookieStore.get(cookies.SSO_REFRESH_TOKEN);
};

/**
 * Get refresh token with SSR support
 * @param req - optional request object for SSR
 */
export const getRefreshTokenSSR = (req?: Request): string | undefined => {
  return req?.cookies?.sso_refresh_token || cookieStore.get(cookies.SSO_REFRESH_TOKEN);
};

/**
 * Check if user is authenticated based on refresh token presence.
 * Refresh token is long-lived (6 months) and is the source of truth for authentication state.
 * @param req - optional request object for SSR
 */
export const isUserAuthenticated = (req?: Request): boolean => {
  const refreshToken = getRefreshTokenSSR(req);
  return !!refreshToken;
};

/**
 * Get the authorization header value with a valid access token.
 * Returns empty string if access token is expired or missing.
 * @param req - optional request object for SSR
 */
export const getAuthorizationHeader = (req?: Request): `Bearer ${string}` | '' => {
  const ciamAccessToken = getCiamAccessToken(req);

  if (ciamAccessToken && !hasTokenExpired(ciamAccessToken)) {
    return `Bearer ${ciamAccessToken}`;
  }
  return '';
};

export const isAccessTokenValid = async (accessToken) => {
  if (!accessToken || hasTokenExpired(accessToken)) {
    return false;
  }
  const isValid: boolean = await pcidAuth.validateToken(accessToken);
  return isValid;
};

export const refreshTokenFlow = async () => {
  const { refresh_token, access_token } = getTokens();
  const isPCIDTokenValid = await isAccessTokenValid(access_token);
  if (!isPCIDTokenValid && refresh_token != null) {
    const tokens = await refreshAccessToken();
    if (tokens !== null) {
      Sentry.captureMessage('Refresh Token successfully');
      return tokens;
    }
  }
  return null;
};

export const refreshAccessToken = async () => {
  try {
    Sentry.addBreadcrumb({ message: 'Refreshing access token via API' });

    const response = await fetch('/api/pcid/token/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-PCID-Initiator': 'client',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return await response.json();
  } catch (e) {
    console.error('Refresh error', e);
    const isAuthError = (e as any)?.error === TOKEN_REFRESH.INVALID_GRANT_ERROR;
    Sentry.captureException(e, {
      tags: {
        error_type: isAuthError ? 'token_refresh_failed' : 'token_refresh_transient_error',
        source: 'client',
        location: 'auth_utils',
        error_code: (e as any)?.error || (e as any)?.code || 'unknown',
      },
      extra: {
        error_description: (e as any)?.error_description,
        full_error: e,
      },
    });

    if (isAuthError) {
      removeTokens();
      if (typeof window !== 'undefined') {
        // Redirect to login page if refresh token flow failed
        window.location.href = ROUTES.LOGIN;
      }
    }
    return null;
  }
};

export const setLastVisitedPage = (pathname: string | null) => {
  if (!pathname?.includes('login') && !pathname?.includes('logout')) {
    try {
      window.localStorage.setItem(cookies.SSO_LAST_VISITED_PAGE, pathname || '');
    } catch (error) {
      Sentry.captureMessage(`Failed to save tokens to localStorage: ${cookies.SSO_LAST_VISITED_PAGE}: ` + error);
      console.error('Failed to save tokens to localStorage: ' + error);
    }
  }
};

export const removeTokens = () => {
  Sentry.setUser(null);
  removeCIAMCookies();
  removeLocalStorageSSOTokens();
  removeOneCheckoutCookies();
};

export const removeCIAMCookies = () => {
  try {
    cookieStore.remove(cookies.CIAM_ACCESS_TOKEN, { path: '/' });
    if (cookieStore.get(cookies.CIAM_ACCESS_TOKEN_GLOBAL)) {
      cookieStore.remove(cookies.CIAM_ACCESS_TOKEN_GLOBAL, { path: '/' });
    }

    //also there can be few browsers with cookies having the old '.www.shoppersdrugmart.ca'
    const domain = !isPharmaprixBanner()
      ? process.env.NEXT_PUBLIC_COOKIE_DOMAIN_EN_URL
      : process.env.NEXT_PUBLIC_COOKIE_DOMAIN_FR_URL;
    cookieStore.remove(cookies.CIAM_ACCESS_TOKEN, { domain });
  } catch (error) {
    Sentry.captureMessage(
      `Failed to remove tokens from cookies: [${cookies.CIAM_ACCESS_TOKEN}, ${cookies.CIAM_ACCESS_TOKEN_GLOBAL}]: ` +
        error
    );
    console.error('Failed to remove token from cookies: ' + error);
  }
};

export const removeLocalStorageSSOTokens = () => {
  try {
    cookieStore.remove(cookies.SSO_REFRESH_TOKEN, { path: '/' });
    window.localStorage.removeItem(cookies.SSO_ID_TOKEN);

    //also there can be few browsers with cookies having the old '.www.shoppersdrugmart.ca'
    const domain = !isPharmaprixBanner()
      ? process.env.NEXT_PUBLIC_COOKIE_DOMAIN_EN_URL
      : process.env.NEXT_PUBLIC_COOKIE_DOMAIN_FR_URL;
    cookieStore.remove(cookies.SSO_REFRESH_TOKEN, { domain });
  } catch (error) {
    Sentry.captureMessage(
      `Failed to remove tokens from localStorage: [${cookies.SSO_REFRESH_TOKEN}, ${cookies.SSO_ID_TOKEN}]: ` + error
    );
    console.error('Failed to save tokens to localStorage: ' + error);
  }
};

export const removeOneCheckoutCookies = () => {
  try {
    // Remove One Checkout cookies with path
    cookieStore.remove(cookies.ONE_CHECKOUT_AUTH_TOKEN, { path: '/' });
    cookieStore.remove(cookies.ONE_CHECKOUT_CART_ID, { path: '/' });
    cookieStore.remove(cookies.ONE_CHECKOUT_LOCALE, { path: '/' });
    cookieStore.remove(cookies.ONE_CHECKOUT_LOB, { path: '/' });

    // Also remove with domain for browsers that may have cookies with domain set
    const domain = isPharmaprixBanner() ? '.pharmaprix.ca' : '.shoppersdrugmart.ca';
    cookieStore.remove(cookies.ONE_CHECKOUT_AUTH_TOKEN, { path: '/', domain, secure: true });
    cookieStore.remove(cookies.ONE_CHECKOUT_CART_ID, { path: '/', domain, secure: true });
    cookieStore.remove(cookies.ONE_CHECKOUT_LOCALE, { path: '/', domain, secure: true });
    cookieStore.remove(cookies.ONE_CHECKOUT_LOB, { path: '/', domain, secure: true });
  } catch (error) {
    Sentry.captureMessage(
      `Failed to remove One Checkout cookies: [${cookies.ONE_CHECKOUT_AUTH_TOKEN}, ${cookies.ONE_CHECKOUT_CART_ID}, ${cookies.ONE_CHECKOUT_LOCALE}, ${cookies.ONE_CHECKOUT_LOB}]: ` +
        error
    );
    console.error('Failed to remove One Checkout cookies: ' + error);
  }
};

export const cleanUp = (domain) => {
  cookieStore.remove(cookies.CIAM_ACCESS_TOKEN_GLOBAL, { domain });
  cookieStore.remove(cookies.CIAM_ACCESS_TOKEN, { domain });
  cookieStore.remove(cookies.SSO_REFRESH_TOKEN, { domain });
  window.localStorage.removeItem(cookies.SSO_REFRESH_TOKEN);
  // Remove One Checkout cookies as well
  cookieStore.remove(cookies.ONE_CHECKOUT_AUTH_TOKEN, { path: '/', domain, secure: true });
  cookieStore.remove(cookies.ONE_CHECKOUT_CART_ID, { path: '/', domain, secure: true });
  cookieStore.remove(cookies.ONE_CHECKOUT_LOCALE, { path: '/', domain, secure: true });
  cookieStore.remove(cookies.ONE_CHECKOUT_LOB, { path: '/', domain, secure: true });
};

export const isAnonymous = (): boolean => {
  return !isUserAuthenticated();
};

export const getLoggedInStatus = (): string => {
  return isUserAuthenticated() ? LoggedInStatus.CURRENT : LoggedInStatus.ANONYMOUS;
};

export const createCartGuidCookie = (guid: string): void => {
  const siteId = getSiteId();
  const requiredDomain = isPharmaprixBanner() ? 'pharmaprix.ca' : 'shoppersdrugmart.ca';
  let expireDate = new Date();
  expireDate.setMonth(expireDate.getMonth() + 12);
  const cookieParameter: CookieSetOptions = { expires: expireDate, path: '/' };
  // Only set domain for production domains
  if (isProductionDomain()) {
    cookieParameter.domain = requiredDomain;
    cookieParameter.secure = true;
  }
  cookieStore.set(`${siteId}-cart-helios`, guid, cookieParameter);
  cookieStore.set(`${siteId}-cart-helios-modified-date`, new Date().getTime().toString(), cookieParameter);
};

export const createCustomerCartGuidCookie = (guid: string): void => {
  const siteId = getSiteId();
  const requiredDomain = isPharmaprixBanner() ? 'pharmaprix.ca' : 'shoppersdrugmart.ca';
  let expireDate = new Date();
  expireDate.setMonth(expireDate.getMonth() + 12);
  const cookieParameter: CookieSetOptions = { expires: expireDate, path: '/' };
  // Only set domain for production domains - QA uses different domains (e.g., lblw.cloud)
  if (isProductionDomain()) {
    cookieParameter.domain = requiredDomain;
    cookieParameter.secure = true;
  }
  cookieStore.set(`${siteId}-customer-cart`, guid, cookieParameter);
  cookieStore.set(`${siteId}-customer-cart-modified-date`, new Date().getTime().toString(), cookieParameter);
};

export const clearCartGuidCookie = (key = 'cart-helios'): void => {
  if (!getIsModernizedBag()) {
    key = 'cart';
  }
  const siteId = getSiteId();
  cookieStore.remove(`${siteId}-${key}`);
};

export const getResTrackingIDCookie = (): string => {
  let trackingId = cookieStore.get(cookies.RES_TRACKINGID);
  let expireDate = new Date();
  expireDate.setMonth(expireDate.getMonth() + 12);
  const cookieParameter: CookieSetOptions = { expires: expireDate, path: '/' };
  if (!trackingId) {
    trackingId = uuidv4();
    cookieStore.set(cookies.RES_TRACKINGID, trackingId, cookieParameter);
  }
  return trackingId;
};

export const getLoggedInUserInfo = (req?: Request): Ciam | null => {
  if (!isUserAuthenticated(req)) {
    return null;
  }
  const accessToken: string | undefined = getCiamAccessToken(req);
  if (!accessToken) {
    return null;
  }
  return jwt_decode<Ciam>(accessToken);
};

export const getLoggedInUserInfoSSR = (token: any): Ciam | null => {
  if (!token || hasTokenExpired(token)) {
    return null;
  }
  const userInfo = jwt_decode(token);
  return userInfo;
};

export const getLoggedInUserEmail = (): string | null | undefined => {
  const userInfo = getLoggedInUserInfo();
  if (!userInfo) {
    return null;
  }
  const { sub } = userInfo;
  return sub;
};

export const hasTokenExpired = (token: string): boolean => {
  const { exp } = jwt_decode<Ciam>(token);
  return Date.now() >= exp * 1000;
};

export const shouldRedirectUser = (userStatus: string): boolean => {
  const isAuthenticated = isUserAuthenticated();
  const redirectUser =
    (userStatus === DATASET_ATTRIBUTES_VALUES.SOFT_LOGIN || userStatus === DATASET_ATTRIBUTES_VALUES.HARD_LOGIN) &&
    !isAuthenticated;
  return redirectUser;
};

export const shouldRedirectToSignIn = (): boolean => {
  return !isUserAuthenticated();
};

export const isUserLoggedIn = (): boolean => {
  if (getLoggedInStatus() === LoggedInStatus.ANONYMOUS) {
    return false;
  } else if (getLoggedInStatus() === LoggedInStatus.CURRENT) {
    const token: string | undefined = getCiamAccessToken();
    if (token) {
      return !hasTokenExpired(token);
    }
  }
  return false;
};

export const getUserCookieStatus = (): string | null => {
  const userData = cookieStore.get(cookies.USER_STATUS);
  return userData ? JSON.parse(decodeURI(userData)) : null;
};

export const setUserCookieStatus = (body: any): string | undefined => {
  return cookieStore.set(cookies.USER_STATUS, encodeURI(JSON.stringify(body)), { path: '/' });
};

export const clearCookie = (key: string): void => cookieStore.remove(key);

export const getAkamaiOriginSessionCookie = () => cookieStore.get(cookies.AKAMAI_ORIGIN_SESSION_COOKIE);

export const getGuid = () => {
  return getLoggedInStatus() === LoggedInStatus.CURRENT ? '' : getExistingCartGuid();
};

// todo: change this to proper return type when we migrate over to helios
export const getForceHeliosCookie = (): any => {
  const forceHelios = cookieStore.get(cookies.FORCE_HELIOS);
  return forceHelios ? { forceHelios } : {};
};

// we can expand on this when we need to
type Ciam = {
  exp: number;
  pcid: string;
  sub?: string;
};
